import React, { useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Route, Switch } from 'react-router';
import { ThemeProvider } from 'styled-components';
import { bindActionCreators, Dispatch } from 'redux';
import { hasBeenStarted, isProgressRestoreFailed } from 'store/course/selectors';
import { getTimerStatus } from 'store/timer/selectors';
import { TimerStatus } from 'constants/timer';
import { RootAppState } from 'store/types';
import { MATERIAL_TYPES } from 'constants/common';
import TimedOut from 'components/popups/timedOut';
import RouteTransition from '../../components/shell/RouteTransition/RouteTransition';
import Layout from '../../components/shell/Layout/Layout';
import { PrivateRoute, RouteWithRedirect } from '../../components/common';
import {
  INDEX_PATH,
  LOGIN_PATH,
  RESULTS_PATH,
  SECTION_PATH,
  SECTIONS_PATH,
  SIGNUP_PATH
} from '../../constants/navigation';
import Sections from './sections/SectionsPage';
import NoAccess from '../errors/noAccess/LimitAccessPage';
import CourseOverview from './courseOverview/CourseOverview';
import Login from '../login/LoginPage';
import SignUp from '../signUp/SignUpPage';
import NotFoundPage from '../errors/notFound/NotFoundPage';
import * as courseActions from '../../store/course/actions';
import { IS_NO_ACCESS, IS_NOT_AUTHORIZED, IS_SUCCESSFUL_ACCESS } from '../../constants/typeAccess';
import { POPUP_ID_START_OVER, POPUP_ID_TIMED_OUT } from '../../constants/popups';
import StartOverPopup from '../../components/popups/startOver/StartOver';
import * as popupActions from '../../store/popup/actions';
import ErrorBoundary from '../../components/errorBoundary/ErrorBoundary';
import { getQuestionPoolSize, isQuestionPoolEnabled } from '../../store/settings/selectors';
import { isIndexView } from '../../components/shell/RouteTransition/viewChecker';
import { getThemeUtils } from 'utils/theme';
import { getAccessibilitySettings } from 'store/accessibility/selectors';

type CourseProps = {
  colors: { [key: string]: any };
  fonts: { [key: string]: any };
  background: { [key: string]: any };
  isLowResolution: boolean;
  isTocExpanded: boolean;
  isUserAuthenticated: boolean;
  location: { [key: string]: any };
  navigateToUrl(url: string): void;
  courseActions: { [key: string]: any };
  userAccessIsLimited: boolean;
  navigateToIndex(): void;
  popupActions: { [key: string]: any };
  isCourseProgressRestoreFailed?: boolean;
  timerStatus: TimerStatus;
  isQuestionPoolEnabled: boolean;
  questionPoolSize: number;
  isCourseStarted: boolean;
  accessibilitySettings: any;
};

const usePrevious = (value: any) => {
  const ref = useRef();
  useEffect(() => {
    ref.current = value;
  });
  return ref.current;
};

const Course = (props: CourseProps) => {
  const {
    colors,
    fonts,
    background,
    isLowResolution,
    isTocExpanded,
    isUserAuthenticated,
    userAccessIsLimited,
    isCourseProgressRestoreFailed,
    timerStatus,
    // eslint-disable-next-line no-shadow
    isQuestionPoolEnabled,
    questionPoolSize,
    isCourseStarted,
    location,
    accessibilitySettings
  } = props;
  let typeAccess = IS_SUCCESSFUL_ACCESS;

  if (userAccessIsLimited || !isUserAuthenticated) {
    typeAccess = userAccessIsLimited && isUserAuthenticated ? IS_NO_ACCESS : IS_NOT_AUTHORIZED;
  }

  const previousProps: any = usePrevious({ timerStatus });

  const openStartOverPopup = () => {
    props.popupActions.openPopup({
      popupId: POPUP_ID_START_OVER,
      popupAriaLabelKey: '[aria label start over popup]',
      component: StartOverPopup,
      startNewAttempt: props.courseActions.startOver,
      navigateToIndex: props.navigateToIndex,
      disablePopupClosing: true,
      disableBottomLine: true
    });
  };

  const openTimedOutPopup = () => {
    props.popupActions.openPopup({
      popupId: POPUP_ID_TIMED_OUT,
      popupAriaLabelKey: '[aria label timed out popup]',
      component: TimedOut,
      disablePopupClosing: true
    });
  };

  useEffect(() => {
    if (isUserAuthenticated) {
      const navigate = async () => {
        const urlToNavigate: any = await props.courseActions.launch();
        props.navigateToUrl(urlToNavigate);
      };

      navigate();
    }
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (isCourseProgressRestoreFailed && isCourseStarted && isIndexView.test(location.pathname)) {
      if (isQuestionPoolEnabled && questionPoolSize <= 0) {
        return;
      }
      openStartOverPopup();
    }

    if (
      timerStatus === TimerStatus.TIMED_OUT &&
      !isCourseProgressRestoreFailed &&
      previousProps?.timerStatus !== timerStatus
    ) {
      openTimedOutPopup();
    }

    // eslint-disable-next-line
  }, [
    isCourseProgressRestoreFailed,
    timerStatus,
    isQuestionPoolEnabled,
    questionPoolSize,
    isCourseStarted,
    location
  ]);

  return (
    <Route
      render={({ location }) => (
        <ThemeProvider
          theme={{ colors, fonts, themeUtils: getThemeUtils({ accessibilitySettings }) }}
        >
          <RouteTransition
            location={location}
            isLowResolution={isLowResolution}
            isTocExpanded={isTocExpanded}
            background={background}
          >
            <Layout className="layout">
              <ErrorBoundary popup={openStartOverPopup}>
                <Switch location={location}>
                  <PrivateRoute
                    exact
                    path={INDEX_PATH}
                    component={Sections}
                    componentNoAccess={NoAccess}
                    typeAccess={typeAccess}
                    materialType={MATERIAL_TYPES.COURSE}
                  />
                  <PrivateRoute
                    exact
                    path={SECTIONS_PATH}
                    component={Sections}
                    componentNoAccess={NoAccess}
                    typeAccess={typeAccess}
                    materialType={MATERIAL_TYPES.COURSE}
                  />
                  <PrivateRoute
                    exact
                    path={RESULTS_PATH}
                    component={CourseOverview}
                    componentNoAccess={NoAccess}
                    typeAccess={typeAccess}
                    materialType={MATERIAL_TYPES.COURSE}
                  />
                  <PrivateRoute
                    path={SECTION_PATH}
                    component={CourseOverview}
                    componentNoAccess={NoAccess}
                    typeAccess={typeAccess}
                    materialType={MATERIAL_TYPES.COURSE}
                  />
                  <RouteWithRedirect
                    exact
                    path={LOGIN_PATH}
                    shouldRedirect={isUserAuthenticated}
                    component={Login}
                  />
                  <RouteWithRedirect
                    exact
                    path={SIGNUP_PATH}
                    shouldRedirect={isUserAuthenticated}
                    component={SignUp}
                  />
                  <Route path="*" exact component={NotFoundPage} />
                </Switch>
              </ErrorBoundary>
            </Layout>
          </RouteTransition>
        </ThemeProvider>
      )}
    />
  );
};

function mapStateToProps(state: RootAppState) {
  return {
    isCourseProgressRestoreFailed: isProgressRestoreFailed(state),
    timerStatus: getTimerStatus(state),
    isQuestionPoolEnabled: isQuestionPoolEnabled(state),
    questionPoolSize: getQuestionPoolSize(state),
    isCourseStarted: hasBeenStarted(state),
    accessibilitySettings: getAccessibilitySettings(state)
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    courseActions: bindActionCreators(courseActions, dispatch),
    popupActions: bindActionCreators(popupActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(Course);
